import { create } from 'zustand';

/**
 * Registers the service worker that keeps the app's own files on the machine,
 * so a reload with no network still opens the app.
 *
 * Two deliberate choices:
 *
 * - **Only in a secure context.** A service worker cannot register over plain
 *   HTTP, which is how the app is still reached on the bare IP. Checking first
 *   means no console error there, just no offline support.
 * - **Never reloads by itself.** A new version waits, and the app offers a
 *   Reload button. Reloading under someone's hands would throw away the very
 *   typing the rest of this work exists to protect.
 */

interface UpdateState {
  /** A new version is downloaded and waiting for a reload. */
  ready: boolean;
  /** Reloads into the new version. */
  apply: () => void;
}

export const useAppUpdate = create<UpdateState>(() => ({
  ready: false,
  apply: () => window.location.reload(),
}));

/** How often to ask whether a new version has been deployed. */
const CHECK_EVERY_MS = 60 * 60_000;

export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return;

  try {
    // Imported here rather than at the top so a browser without service
    // workers never loads the plugin's helper at all.
    const { registerSW } = await import('virtual:pwa-register');

    const updateApp = registerSW({
      onNeedRefresh() {
        useAppUpdate.setState({ ready: true });
      },
      onRegisteredSW(_url, registration) {
        if (!registration) return;
        // Deploys are occasional; an hourly check is enough to notice one
        // without asking the server for nothing all day.
        setInterval(() => void registration.update(), CHECK_EVERY_MS);
      },
    });

    useAppUpdate.setState({
      apply: () => {
        // Hands over to the waiting worker, which then reloads the page.
        void updateApp(true);
      },
    });
  } catch {
    // No offline support is a loss, never a reason to stop the app loading.
  }
}
